import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import { DraftCheckpoint, DraftMethods } from './draftSignals';

export interface DraftArray<T> {
    signal : SDataArray<T>;
    methods : DraftMethods;
}

/**
 * A function that can be supplied as the `merge` parameter to `DraftArray`. This can be a good choice of
 * `merge` function for making a draft version of a collection of **one-to-many** child entities. With this
 * `merge` value, after an update to the original array, the `DraftArray` will contain...
 * - All items from the original array that have not been removed in the draft version. This includes any new
 *   items that have yet to appear in the draft version. These items are sorted as they appear in the original
 *   array.
 * - Next, all items from the draft array that do not appear in the original array. The "do not appear" test
 *   is based on **object identity.** These items are sorted as they appear in the draft array.
 */
export function MergeByObjectIdentity<T>(fromOriginal : T[], fromDraft : T[]) {
    const inOriginal = new Set(fromOriginal);
    return fromOriginal.concat(fromDraft.filter(item => !inOriginal.has(item)));
}

/**
 * Produces a function that can be supplied as the `merge` parameter to `DraftArray`. This can be a good choice
 * of `merge` function for making a draft version of a collection of **many-to-many** child entities. With this
 * `merge` value, after an update to the original array, the `DraftArray` will contain...
 * - All items from the original array that have not been removed in the draft version. This includes any new
 *   items that have yet to appear in the draft version. These items are sorted as they appear in the original
 *   array.
 * - Next, all items from the draft array that do not appear in the original array. The "do not appear" test
 *   is based on the provided `keyFn`. These items are sorted as they appear in the draft array.
 * @param keyFn A key function to use for the "do not appear" test (see above). E.g., if making a draft version
 * of a collection of many-to-many child entities, the key function would reference the child entities' FK
 * property pointing to the **other** side of the many-to-many relationship.
 */
export function MergeByKey<T>(keyFn : (item : T) => unknown) {
    return (fromOriginal : T[], fromDraft : T[]) => {
        const inOriginal = new Set(fromOriginal.map(item => keyFn(item)));
        return fromOriginal.concat(fromDraft.filter(item => !inOriginal.has(keyFn(item))));
    };
}

/**
 * A utility for maintaining a draft version of an array-valued signal. Similar to the functionality provided
 * by our `DraftSignal` utility, with some key differences:
 * - The signal produced by this utility satisfies the full `SArray.ArrayDataSignal` interface.
 * - When a `DraftSignal` is in caching mode, it ignores **all** changes to the original signal. In contrast,
 *   even when this `DraftArray` signal is in caching mode, it can still accept updates from the original array
 *   signal, subject to a few limitations:
 *   - This draft array signal maintains a cache of items from the original array signal that have been removed
 *     from the draft version of the array. Those removed items will continue to be absent from the draft array
 *     while it remains in caching mode. This cache of removed items is keyed by **object identity.**
 *   - The exact behavior by which a `DraftArray` in caching mode accepts updates from the original signal is
 *     controlled by the `merge` parameter to `DraftArray`.
 */
export function DraftArray<T extends object>(
    original : DataSignal<T[]>,
    merge : (fromOriginal : T[], fromDraft : T[]) => T[]
) : DraftArray<T> {
    const
        isCaching = S.value(false),
        // CAUTION: Have to .slice() here and in several places below to avoid our draft array signal
        // inadvertently referencing the same internal JS array as our original array signal. That's b/c
        // SArrays handle mutations by mutating their internal JS array, so if our original and draft signals
        // referenced the same internal JS array, we'd get all kinds of weirdness about mutations to one signal
        // becoming immediately visible when reading the OTHER signal.
        draft = SArray(S.sample(original).slice());

    // Items from the original array that have been removed in the draft version
    let removed = new WeakSet<T>();

    // Various flags...
    // - For tracking checkpoints...
    let version = 0, committedVersion = 0;
    // - For ignoring changes to our original array if we know those changes to originate from us...
    let origCompVersion = 0, ignoredOrigCompVersion = 0;
    // - For ignoring changes to our draft array if we know those changes to originate from us...
    let draftCompVersion = 0, ignoredDraftCompversion = 0;

    // Update the draft array as the original array changes
    S.on(original, () => {
        if (origCompVersion++ !== ignoredOrigCompVersion) {
            draft(isCaching() ?
                merge(original().filter(item => !removed.has(item)), draft()) :
                // CAUTION: Here's another one of those .slice() spots. See above.
                original().slice()
            );
            // Ignore this write to our draft array
            ignoredDraftCompversion = draftCompVersion;
        }
    });

    S.on(draft, prevDraft => {
        const _draft = draft();
        if (draftCompVersion++ !== ignoredDraftCompversion && !arrayEq(_draft, prevDraft)) {
            S.freeze(() => {
                version++;
                isCaching(true);

                const inLatest = new Set(_draft);
                // Item can't be removed if it's shown (back) up in draft array.
                for (let item of _draft) removed.delete(item);
                // If item was previously in the draft array and now isn't, mark it as removed.
                for (let item of prevDraft) {
                    if (!inLatest.has(item)) removed.add(item);
                }
            });
        }
        // CAUTION: Here's another one of those .slice() spots. In this case, if we didn't .slice(), then the
        // next time S ran our computation function, prevDraft would evaluate to the SAME internal JS array
        // referenced by our draft array signal, so it would ALREADY contain the mutations visible in our
        // draft array signal. That would defeat the whole purpose of taking prevDraft as a parameter here.
        return _draft.slice();
    }, S.sample(draft).slice()); // Gotta .slice() in our seed value too!

    return {
        signal: draft,
        methods: {
            isCaching: () => isCaching(),
            onWrite: () => { draft(); },
            checkpoint
        }
    };

    function checkpoint() : DraftCheckpoint {
        const
            // CAUTION: Here's yet another .slice() spot. In this case, if we didn't .slice(), then
            // a) We might try to set our original array signal to the same internal JS array as our draft
            //    array signal when we commit this checkpoint, which would give rise to the same problem of
            //    sharing an internal JS array mentioned above.
            // b) If calling code were to mutate our draft array signal after this checkpoint is taken but
            //    before it's committed, then those mutations would be visible in the array we pass into our
            //    original array signal, which would bypass our version checking. This is why we need to
            //    .slice() here and NOT inside our commit method below.
            checkpointValue = S.sample(draft).slice(),
            checkpointVersion = version;

        return { commit, revert };

        function commit() {
            S.freeze(() => {
                if (committedVersion < checkpointVersion) {
                    committedVersion = checkpointVersion;
                    original(checkpointValue);
                    // Ignore this write to our original array
                    ignoredOrigCompVersion = origCompVersion;
                }
                if (checkpointVersion === version) {
                    removed = new WeakSet();
                    isCaching(false);
                }
            });
        }

        function revert() {
            if (checkpointVersion === version && S.sample(isCaching)) {
                S.freeze(() => {
                    removed = new WeakSet();
                    isCaching(false);
                    // CAUTION: Here's yet another .slice() spot.
                    draft(S.sample(original).slice());
                    // Ignore this write to our draft array
                    ignoredDraftCompversion = draftCompVersion;
                });
            }
        }
    }
}

function arrayEq<T>(a1 : T[], a2 : T[]) {
    if (a1.length !== a2.length) return false;
    for (let i = 0; i < a1.length; i++) {
        if (a1[i] !== a2[i]) return false;
    }
    return true;
}



// WEBPACK FOOTER //
// ./lib/draftArray.ts